import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/authContext';
import { updateAgentSettings, updateUserProfile } from '../services/api';
import { getSubscriptionStatus } from '../services/subscriptionService';
import { Link } from 'react-router-dom';
import { PageHeading, Heading, Text } from '../components/ui';

const SousChefPage = () => {
    const { user, userProfile, refreshUserProfile } = useAuth();
    
    const [agentSettings, setAgentSettings] = useState({
        name: 'Chef',
        trigger: 'hey chef',
        enabled: true
    });
    const [chefFeatures, setChefFeatures] = useState({
        reminders: false,
        summaries: false,
        recommendations: false
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [subscription, setSubscription] = useState(null);

    useEffect(() => {
        const fetchSubscription = async () => {
            try {
                const status = await getSubscriptionStatus();
                setSubscription(status);
            } catch (err) {
                console.error('Failed to fetch subscription:', err);
            }
        };
        fetchSubscription();
    }, []);

    useEffect(() => {
        if (userProfile) {
            setAgentSettings({
                name: userProfile.agent_name || 'Chef',
                trigger: userProfile.agent_trigger || 'hey chef',
                enabled: userProfile.agent_enabled !== false
            });
            setChefFeatures({
                reminders: userProfile?.agent_preferences?.chef_reminders || false,
                summaries: userProfile?.agent_preferences?.chef_summaries || false,
                recommendations: userProfile?.agent_preferences?.chef_recommendations || false
            });
            setLoading(false);
        }
    }, [userProfile]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        try {
            const token = await user.getIdToken();
            
            // Update agent settings
            const agentUpdateData = { 
                agent_name: agentSettings.name,
                agent_trigger: agentSettings.trigger,
                agent_enabled: agentSettings.enabled
            };
            await updateAgentSettings(agentUpdateData, token);
            
            // Update chef features
            const profileUpdateData = {
                agent_preferences: {
                    chef_reminders: chefFeatures.reminders,
                    chef_summaries: chefFeatures.summaries,
                    chef_recommendations: chefFeatures.recommendations
                }
            };
            await updateUserProfile(user.uid, profileUpdateData, token);
            
            // Refresh the user profile in auth context
            await refreshUserProfile();
            
            setSuccess('Sous chef settings updated successfully!');
            
        } catch (err) {
            console.error('Failed to update sous chef settings:', err);
            setError('Failed to update sous chef settings. Please try again.');
        }
    };

    // Color tokens for consistent styling
    const colorTokens = {
        oxfordBlue: '#1b263b',
        alabaster: '#EEEEE7',
        white: '#FFFFFF',
        richBlack: '#0d1b2a'
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-neutral-50">
                <div className="w-full px-0 py-8">
                    <div className="bg-neutral-0 hairline minimal-padding">
                        <Text>Loading sous chef settings...</Text>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-neutral-50">
            <div className="w-full px-0 py-8">
                {/* Header */}
                <PageHeading 
                    size="hero"
                    subtitle="AI ASSISTANT & FEATURE CONFIGURATION"
                >
                    SOUS CHEF SETTINGS
                </PageHeading>

                {/* Navigation Buttons - Outside Card */}
                <div className="max-w-6xl mx-auto mb-6">
                    <div className="flex space-x-4">
                        <Link
                            to="/profile"
                            className="px-4 py-2 bg-neutral-0 text-neutral-900 hairline hover:bg-neutral-50 transition-colors font-mono text-sm uppercase tracking-wider"
                        >
                            BACK TO PROFILE
                        </Link>
                        <Link
                            to="/activity"
                            className="px-4 py-2 bg-neutral-0 text-neutral-900 hairline hover:bg-neutral-50 transition-colors font-mono text-sm uppercase tracking-wider"
                        >
                            COOKING ACTIVITY
                        </Link>
                        <Link
                            to="/grocery/stock"
                            className="px-4 py-2 bg-neutral-0 text-neutral-900 hairline hover:bg-neutral-50 transition-colors font-mono text-sm uppercase tracking-wider"
                        >
                            PANTRY
                        </Link>
                    </div>
                </div>

                {/* Success/Error Messages */}
                {(success || error) && (
                    <div className="max-w-6xl mx-auto mb-6">
                        <div className="bg-neutral-0 hairline minimal-padding vspace">
                            {success && (
                                <div className="bg-green-50 p-4 hairline mb-4">
                                    <Text className="text-green-900">{success}</Text>
                                </div>
                            )}
                            {error && (
                                <div className="bg-red-50 p-4 hairline mb-4">
                                    <Text className="text-red-900">{error}</Text>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Two Column Layout */}
                <div className="max-w-6xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Left Column - Voice Assistant Settings */}
                        <div className="bg-neutral-0 hairline p-6">
                            <div className="space-y-6">
                                {/* Sous Chef Header */}
                                <div className="text-center mb-6">
                                    <Heading level={2} className="text-2xl font-medium mb-2" style={{ color: colorTokens.oxfordBlue }}>
                                        Voice Assistant
                                    </Heading>
                                    <div className="flex justify-center items-center space-x-4 text-sm font-mono text-neutral-900 opacity-60">
                                        <span>{agentSettings.name || 'Sous Chef'}</span>
                                        <span>•</span>
                                        <span>{agentSettings.enabled ? 'ENABLED' : 'DISABLED'}</span>
                                    </div>
                                </div>

                                {/* Voice Assistant Settings Form */}
                                <form onSubmit={handleSubmit} className="space-y-6">
                                    <div>
                                        <label className="block font-mono text-sm uppercase tracking-wider text-neutral-900 mb-2">
                                            SOUS CHEF NAME
                                        </label>
                                        <input
                                            type="text"
                                            value={agentSettings.name}
                                            onChange={(e) => setAgentSettings(prev => ({
                                                ...prev,
                                                name: e.target.value
                                            }))}
                                            placeholder="Enter sous chef name"
                                            className="w-full px-3 py-2 hairline bg-neutral-0 focus:outline-none focus:bg-neutral-50"
                                        />
                                        <Text size="sm" className="mt-1 opacity-60">
                                            The name your AI sous chef will use to refer to itself during live cooking sessions.
                                        </Text>
                                    </div>
                                    
                                    <div>
                                        <label className="block font-mono text-sm uppercase tracking-wider text-neutral-900 mb-2">
                                            TRIGGER COMMAND
                                        </label>
                                        <input
                                            type="text"
                                            value={agentSettings.trigger}
                                            onChange={(e) => setAgentSettings(prev => ({
                                                ...prev,
                                                trigger: e.target.value
                                            }))}
                                            placeholder="hey chef"
                                            className="w-full px-3 py-2 hairline bg-neutral-0 focus:outline-none focus:bg-neutral-50"
                                        />
                                        <Text size="sm" className="mt-1 opacity-60">
                                            The phrase you'll say to activate voice commands. Try saying "{agentSettings.trigger}" to test.
                                        </Text>
                                    </div>
                                    
                                    <div className="flex items-center space-x-3">
                                        <input
                                            type="checkbox"
                                            id="agentEnabled"
                                            checked={agentSettings.enabled}
                                            onChange={(e) => setAgentSettings(prev => ({
                                                ...prev,
                                                enabled: e.target.checked
                                            }))}
                                            className="w-4 h-4"
                                        />
                                        <label htmlFor="agentEnabled" className="font-mono text-sm uppercase tracking-wider text-neutral-900">
                                            ENABLE VOICE ASSISTANT
                                        </label>
                                    </div>
                                    <Text size="sm" className="opacity-60 ml-7">
                                        Enable or disable the voice assistant during live cooking sessions. When disabled, voice commands will not be processed.
                                    </Text>
                                </form>
                            </div>
                        </div>

                        {/* Right Column - Chef Features */}
                        <div className="bg-neutral-0 hairline p-6">
                            <div className="space-y-6">
                                {/* Chef Features Header */}
                                <div className="text-center mb-6">
                                    <Heading level={2} className="text-2xl font-medium mb-2" style={{ color: colorTokens.oxfordBlue }}>
                                        Chef Features
                                    </Heading>
                                    <Text className="text-sm opacity-60">
                                        Control which AI-powered chef features are enabled for your account.
                                    </Text>
                                    {subscription && subscription.planType === 'free' && (
                                        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200">
                                            <Text className="text-sm text-yellow-800">
                                                💡 Chef agent features are available with Basic plan and above. <Link to="/profile?tab=subscription" className="underline font-medium">Upgrade now</Link> to unlock custom chef interactions, AI tips, reminders, and summaries.
                                            </Text>
                                        </div>
                                    )}
                                </div>

                                {/* Chef Features Toggles */}
                                <div className="space-y-4">
                                    {/* Chef Reminders Toggle */}
                                    <div className={`flex items-center justify-between p-4 bg-neutral-50 hairline ${subscription && subscription.planType === 'free' ? 'opacity-50' : ''}`}>
                                        <div className="flex-1">
                                            <Text className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-1">
                                                CHEF REMINDERS
                                            </Text>
                                            <Text className="text-sm opacity-60">
                                                Get helpful reminders during cooking sessions
                                            </Text>
                                        </div>
                                        <label className="checkbox-label">
                                            <input
                                                type="checkbox"
                                                checked={chefFeatures.reminders}
                                                onChange={(e) => setChefFeatures(prev => ({
                                                    ...prev,
                                                    reminders: e.target.checked
                                                }))}
                                                disabled={subscription && subscription.planType === 'free'}
                                                className="checkbox-input"
                                            />
                                            <span className="checkbox-mark"></span>
                                        </label>
                                    </div>

                                    {/* Chef Summaries Toggle */}
                                    <div className={`flex items-center justify-between p-4 bg-neutral-50 hairline ${subscription && subscription.planType === 'free' ? 'opacity-50' : ''}`}>
                                        <div className="flex-1">
                                            <Text className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-1">
                                                CHEF SUMMARIES
                                            </Text>
                                            <Text className="text-sm opacity-60">
                                                Receive AI-generated summaries of your cooking sessions
                                            </Text>
                                        </div>
                                        <label className="checkbox-label">
                                            <input
                                                type="checkbox"
                                                checked={chefFeatures.summaries}
                                                onChange={(e) => setChefFeatures(prev => ({
                                                    ...prev,
                                                    summaries: e.target.checked
                                                }))}
                                                disabled={subscription && subscription.planType === 'free'}
                                                className="checkbox-input"
                                            />
                                            <span className="checkbox-mark"></span>
                                        </label>
                                    </div>

                                    {/* Chef Recommendations Toggle */}
                                    <div className={`flex items-center justify-between p-4 bg-neutral-50 hairline ${subscription && subscription.planType === 'free' ? 'opacity-50' : ''}`}>
                                        <div className="flex-1">
                                            <Text className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-1">
                                                CHEF RECOMMENDATIONS
                                            </Text>
                                            <Text className="text-sm opacity-60">
                                                Get personalized recipe and cooking recommendations
                                            </Text>
                                        </div>
                                        <label className="checkbox-label">
                                            <input
                                                type="checkbox"
                                                checked={chefFeatures.recommendations}
                                                onChange={(e) => setChefFeatures(prev => ({
                                                    ...prev,
                                                    recommendations: e.target.checked
                                                }))}
                                                disabled={subscription && subscription.planType === 'free'}
                                                className="checkbox-input"
                                            />
                                            <span className="checkbox-mark"></span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Update Button - Full Width Below Columns */}
                    <div className="mt-6">
                        <form onSubmit={handleSubmit}>
                            <button
                                type="submit"
                                className="w-full px-6 py-3 bg-neutral-900 text-neutral-0 hairline font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity"
                            >
                                UPDATE SOUS CHEF SETTINGS
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SousChefPage;
